import { lazy, Suspense, useCallback, useEffect, useRef } from "react";
import { BrowserRouter, Link, Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import { MegaSearch } from "@/components/MegaSearch";
import { CreateButtons } from "@/components/CreateButtons";
import { Sidebar, Wordmark } from "@/components/Sidebar";
import { MobileNav } from "@/components/shell/MobileNav";
import { ScenarioBanner } from "@/components/shell/ScenarioBanner";
import { useTrackRecent } from "@/components/shell/recent";
import { useScrollRestore } from "@/components/shell/useScrollRestore";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Toaster } from "@/components/ui/sonner";
import { setUnauthorizedHandler } from "@/api/http";
import { useLiveUpdates } from "@/api/live";
import { ErrorBoundary } from "@/components/shell/ErrorBoundary";
import { NetworkBanner } from "@/components/shell/NetworkBanner";
import { StaleForecastDialog } from "@/components/shell/StaleForecastDialog";
import { useScenarioInUrl } from "@/components/shell/scenarioUrl";
import { useStore } from "@/model/store";
import { go } from "@/lib/nav";
import { Booting, ForgotPage, Landing, ResetPage, SignInPage, SignUpPage, VerifyPage } from "@/pages/AuthPages";
import { HomePage } from "@/pages/HomePage";
import { ScenarioPage } from "@/pages/ScenarioPage";
import { NodePage } from "@/pages/NodePage";
import { NotFoundPage } from "@/pages/NotFoundPage";

// Pages most visits never open load on demand; the Graph page brings the graph libraries with it (UI plan §9).
const GraphPage = lazy(() => import("@/pages/GraphPage").then((m) => ({ default: m.GraphPage })));
const AccountPage = lazy(() => import("@/pages/AccountPage").then((m) => ({ default: m.AccountPage })));
const ActivityPage = lazy(() => import("@/pages/ActivityPage").then((m) => ({ default: m.ActivityPage })));
const ChangesetPage = lazy(() => import("@/pages/ActivityPage").then((m) => ({ default: m.ChangesetPage })));
const ConsentPage = lazy(() => import("@/pages/ConsentPage").then((m) => ({ default: m.ConsentPage })));

function useUndoKeys() {
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			const target = e.target as HTMLElement;
			if (target instanceof Element && target.closest('input, textarea, select, [contenteditable]:not([contenteditable="false"])')) return;
			if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "z") {
				e.preventDefault();
				if (e.shiftKey) void useStore.getState().redo();
				else void useStore.getState().undo();
			}
		};
		window.addEventListener("keydown", onKey);
		return () => window.removeEventListener("keydown", onKey);
	}, []);
}

/** The scrolling main pane with the header (menu, search, create) and the active-scenario bar. */
function Main({ children }: { children: React.ReactNode }) {
	const ref = useRef<HTMLElement>(null);
	useScrollRestore(ref);
	useTrackRecent();
	useScenarioInUrl();
	const { pathname } = useLocation();
	return (
		<main ref={ref} className="min-w-0 flex-1 overflow-y-auto">
			<header className="sticky top-0 z-30 border-b bg-background/85 backdrop-blur">
				<div className="mx-auto flex max-w-[1400px] flex-wrap items-center gap-x-3 gap-y-2 px-4 py-3 lg:px-8">
					<MobileNav />
					<Link to="/" className="shrink-0 lg:hidden" aria-label="ModelCurve, Worldview">
						<Wordmark />
					</Link>
					<div className="order-last flex w-full min-w-0 justify-center sm:order-none sm:w-auto sm:flex-1">
						<MegaSearch />
					</div>
					<div className="ml-auto sm:ml-0">
						<CreateButtons />
					</div>
				</div>
				<ScenarioBanner />
				<NetworkBanner />
			</header>
			{/* Keyed by path: a page that crashed resets when you leave it. */}
			<ErrorBoundary key={pathname}>
				<Suspense fallback={<p className="p-10 text-muted-foreground">Loading…</p>}>{children}</Suspense>
			</ErrorBoundary>
		</main>
	);
}

function Page() {
	return (
		<div className="mx-auto max-w-[1400px] px-4 py-5 lg:px-8 lg:py-8">
			<Routes>
				<Route path="/" element={<HomePage />} />
				<Route path="/scenario/:scenarioId" element={<ScenarioPage />} />
				<Route path="/node/:nodeId" element={<NodePage />} />
				<Route path="/activity" element={<ActivityPage />} />
				<Route path="/activity/:changesetId" element={<ChangesetPage />} />
				<Route path="/account" element={<AccountPage />} />
				<Route path="*" element={<NotFoundPage />} />
			</Routes>
			<footer className="mt-16 border-t pt-4 text-xs text-muted-foreground">Forecasts are opinions and models, not facts or advice.</footer>
		</div>
	);
}

/** One workspace: `/w/:ws/…` is this router's basename, so in-app links are `/node/:handle`, `/scenario/:id`, `/graph`. */
function WorkspaceApp({ ws }: { ws: string }) {
	const phase = useStore((s) => s.phase);
	const loaded = useStore((s) => s.ws === ws && s.revision !== null);
	const loadError = useStore((s) => s.loadError);
	const workspaces = useStore((s) => s.workspaces);
	useUndoKeys();

	useEffect(() => {
		if (phase === "signed-out") go(`/signin?next=${encodeURIComponent(window.location.pathname + window.location.search)}`);
		if (phase === "signed-in") void useStore.getState().enterWorkspace(ws);
	}, [phase, ws]);

	if (phase !== "signed-in") return <Booting />;
	if (!loaded) {
		if (!loadError) return <Booting message="Loading your worldview…" />;
		const own = workspaces[0];
		return (
			<Booting
				message={
					<span>
						{loadError}{" "}
						{own && own.slug !== ws && (
							<a className="font-medium text-foreground underline underline-offset-2" href={`/w/${own.slug}/`}>
								Open {own.name}
							</a>
						)}
					</span>
				}
			/>
		);
	}
	return (
		<BrowserRouter basename={`/w/${encodeURIComponent(ws)}`}>
			<Live ws={ws} />
			<StaleForecastDialog />
			<div className="flex h-screen overflow-hidden">
				<Sidebar />
				<Main>
					<Routes>
						{/* The graph canvas is the page: full bleed, no footer. */}
						<Route path="/graph" element={<GraphPage />} />
						<Route path="*" element={<Page />} />
					</Routes>
				</Main>
			</div>
		</BrowserRouter>
	);
}

function Live({ ws }: { ws: string }) {
	const navigate = useNavigate();
	const openActivity = useCallback((id: string) => navigate(`/activity/${id}`), [navigate]);
	useLiveUpdates(ws, openActivity);
	return null;
}

/** Sign-in, sign-up, verification, password reset and the OAuth consent screen. */
function PublicApp() {
	return (
		<BrowserRouter>
			<Routes>
				<Route path="/signin" element={<SignInPage />} />
				<Route path="/signup" element={<SignUpPage />} />
				<Route path="/verify" element={<VerifyPage />} />
				<Route path="/forgot" element={<ForgotPage />} />
				<Route path="/reset" element={<ResetPage />} />
				<Route
					path="/oauth/consent"
					element={
						<Suspense fallback={<Booting />}>
							<ConsentPage />
						</Suspense>
					}
				/>
				<Route path="/account" element={<AccountRedirect />} />
				<Route path="*" element={<Landing />} />
			</Routes>
		</BrowserRouter>
	);
}

/** `/account` outside a workspace: open it in the user's own workspace, where the app's shell is. */
function AccountRedirect() {
	const phase = useStore((s) => s.phase);
	const workspaces = useStore((s) => s.workspaces);
	if (phase === "booting") return <Booting />;
	if (phase === "signed-out") return <Navigate to="/signin?next=%2Faccount" replace />;
	const ws = workspaces.find((w) => w.kind === "personal") ?? workspaces[0];
	if (ws) window.location.replace(`/w/${encodeURIComponent(ws.slug)}/account`);
	return <Booting />;
}

function workspaceInPath(): string | null {
	const m = /^\/w\/([^/]+)/.exec(window.location.pathname);
	return m ? decodeURIComponent(m[1]) : null;
}

export default function App() {
	useEffect(() => {
		setUnauthorizedHandler(() => {
			if (useStore.getState().phase === "signed-in") useStore.setState({ phase: "signed-out" });
		});
		void useStore.getState().bootstrap();
	}, []);
	const ws = workspaceInPath();
	return (
		<TooltipProvider>
			{ws ? <WorkspaceApp ws={ws} /> : <PublicApp />}
			<Toaster position="bottom-center" />
		</TooltipProvider>
	);
}
